import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton } from "@/components/ios";
import { SignaturePad } from "@/components/SignaturePad";
import { Building2, Sparkles, AlertTriangle, Loader2, Image as ImageIcon, Map, Download, CheckCircle2 } from "lucide-react";

const SEV_TONE = { minor: "warning", moderate: "warning", major: "danger" };

export default function OwnerReport() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [gallery, setGallery] = useState([]);
  const [dl, setDl] = useState(false);
  const [sigName, setSigName] = useState("");
  const [sig, setSig] = useState(null);
  const [signing, setSigning] = useState(false);

  const signAccept = async () => {
    if (!sigName.trim() || !sig) return;
    setSigning(true);
    try {
      await axios.post(`${API}/owner/${token}/sign`, { name: sigName.trim(), role: "owner", signature_image: sig });
      const { data } = await axios.get(`${API}/owner/${token}`);
      setD(data); setGallery(data.gallery || []);
    } catch (e) {}
    finally { setSigning(false); }
  };

  useEffect(() => {
    axios.get(`${API}/owner/${token}`).then(({ data }) => {
      setD(data);
      setGallery(data.gallery || []);
    }).catch(() => setErr("This link is invalid or has expired."));
  }, [token]);

  const downloadPdf = async () => {
    setDl(true);
    try {
      const { data } = await axios.get(`${API}/owner/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(d?.property?.address || "report").split(",")[0].trim().replace(/\s+/g, "-")}-report.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {}
    finally { setDl(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center text-secondary" style={{ background: "var(--bg)" }}>{err}</div>;
  if (!d) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg)" }}><Loader2 className="animate-spin accent-text" /></div>;

  const { report: r, property: p, agency } = d;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className="h-56 bg-cover bg-center relative" style={{ backgroundImage: `url(${p?.image})` }}>
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.2))" }} />
        <div className="absolute top-5 left-5 flex items-center gap-2 text-white">
          <div className="h-9 w-9 rounded-xl grid place-items-center overflow-hidden accent-fg" style={{ background: agency?.logo_url ? "#fff" : (agency?.accent_color || "var(--accent)") }}>
            {agency?.logo_url ? <img src={agency.logo_url} alt="logo" className="h-full w-full object-contain" /> : (agency?.brand_initials || <Building2 size={18} />)}
          </div>
          <span className="font-semibold">{agency?.name}</span>
        </div>
        <div className="absolute bottom-5 left-5 text-white">
          <div className="text-sm opacity-80">Inspection summary for landlord</div>
          <h1 className="text-3xl font-bold tracking-tight">{p?.address}</h1>
          <div className="opacity-80">{p?.suburb}, {p?.state} {p?.postcode}</div>
        </div>
        <button data-testid="owner-download-pdf" onClick={downloadPdf} disabled={dl}
          className="absolute top-5 right-5 flex items-center gap-1.5 text-sm font-semibold text-white rounded-full px-4 py-2 backdrop-blur"
          style={{ background: "rgba(0,0,0,0.35)" }}>
          <Download size={15} /> {dl ? "Preparing…" : "Download PDF"}
        </button>
      </div>

      <main className="max-w-2xl mx-auto px-5 py-6 -mt-6 relative">
        <Surface className="p-6">
          <div className="flex items-center gap-2 text-sm font-medium text-secondary mb-2"><Sparkles size={15} className="accent-text" /> Plain-English summary</div>
          <p className="leading-relaxed text-lg" style={{ color: "var(--text)" }}>{r.owner_summary_text || r.overall_summary}</p>
        </Surface>

        <h2 className="text-xl font-semibold tracking-tight mt-6 mb-3" style={{ color: "var(--text)" }}>Maintenance recommendations</h2>
        {(r.findings || []).length === 0 ? (
          <Surface className="p-5 text-sm text-secondary">No issues requiring attention were identified. The property is in good condition.</Surface>
        ) : (
          <div className="space-y-3">
            {r.findings.map((f, i) => (
              <Surface key={i} className="p-4 flex items-start gap-3">
                <AlertTriangle size={18} style={{ color: SEV_TONE[f.severity] === "danger" ? "var(--danger)" : "var(--warning)" }} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2"><span className="font-medium" style={{ color: "var(--text)" }}>{f.room} — {f.label}</span><Pill tone={SEV_TONE[f.severity] || "warning"}>{f.severity}</Pill></div>
                  <p className="text-sm text-secondary mt-0.5">{f.comment}</p>
                </div>
              </Surface>
            ))}
          </div>
        )}

        {d.floorplan && (d.plan_pins || []).length > 0 && (
          <>
            <h2 className="text-xl font-semibold tracking-tight mt-6 mb-3 flex items-center gap-2" style={{ color: "var(--text)" }}><Map size={18} /> Findings map</h2>
            <Surface className="p-3">
              <div className="relative">
                <img src={mediaUrl(d.floorplan.signed_url)} className="w-full rounded-xl" alt="floor plan" />
                {d.plan_pins.map((p, i) => (
                  <div key={i} className="absolute -translate-x-1/2 -translate-y-full" style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}>
                    <div className="h-6 w-6 rounded-full grid place-items-center text-white text-[10px] font-bold ring-2 ring-white shadow-lg"
                      style={{ background: p.severity === "major" ? "var(--danger)" : p.severity === "none" ? "var(--text-secondary)" : "var(--warning)" }}
                      title={`${p.room} — ${p.label} (${p.wall} wall)`}>{i + 1}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-1">
                {d.plan_pins.map((p, i) => (
                  <div key={i} className="text-xs text-secondary flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full grid place-items-center text-white text-[9px] font-bold shrink-0" style={{ background: p.severity === "major" ? "var(--danger)" : "var(--warning)" }}>{i + 1}</span>
                    {p.room} — {p.label} · {p.wall} wall
                  </div>
                ))}
              </div>
            </Surface>
          </>
        )}

        {gallery.length > 0 && (
          <>
            <h2 className="text-xl font-semibold tracking-tight mt-6 mb-3 flex items-center gap-2" style={{ color: "var(--text)" }}><ImageIcon size={18} /> Key photos</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {gallery.map((m) => <img key={m.id} src={mediaUrl(m.signed_url)} className="w-full aspect-square object-cover rounded-xl" alt="evidence" />)}
            </div>
          </>
        )}

        {r.signatures?.some((s) => s.party === "owner") ? (
          <Surface className="p-6 mt-6 text-center">
            <CheckCircle2 size={36} className="accent-text mx-auto" />
            <div className="font-semibold mt-2" style={{ color: "var(--text)" }}>You've acknowledged this report</div>
            <div className="text-sm text-secondary">Recorded {new Date((r.signatures.find((s) => s.party === "owner") || {}).at).toLocaleString("en-AU")}</div>
          </Surface>
        ) : (
          <Surface className="p-5 mt-6">
            <div className="font-medium mb-1" style={{ color: "var(--text)" }}>Acknowledge this report</div>
            <p className="text-xs text-secondary mb-3">Sign to confirm you've received and reviewed this inspection summary.</p>
            <input value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder="Your full name" data-testid="owner-sign-name"
              className="w-full text-sm rounded-lg px-3 py-2 outline-none border mb-2" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />
            <SignaturePad testid="owner-signature" onChange={setSig} />
            <AButton testid="owner-sign-btn" className="w-full mt-3" onClick={signAccept} disabled={signing}>{signing ? "Saving…" : "Sign & acknowledge"}</AButton>
          </Surface>
        )}

        <p className="text-xs text-secondary text-center mt-8">This summary is grounded strictly in the approved condition report. {r.report_type} inspection · {new Date(r.created_at).toLocaleDateString("en-AU")}</p>
      </main>
    </div>
  );
}
